import { usePersistedState } from "@/lib/persistedFilters";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, ChevronRight, Flame, AlertTriangle, Search } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Avatar";
import { ConflictBadge, handoverTitle } from "@/components/PresenceChips";
import { STATUS_META, ORDER, roleLabel, fmtRelative } from "@/components/WhoIsInCard";
import { PersonWeekDialog } from "@/components/PresenceHistory";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { TeamFilter } from "@/components/TeamFilter";

// Iter 133 — the slide-out is list-only now; the card view lives on the full
// In/Out app page (see components/WhoIsInCard.jsx). Status metadata, labels and
// the relative-time helper are shared from there.

export const WhoIsInDrawer = () => {
  const { user, has } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [staff, setStaff] = useState([]);
  const [historyRow, setHistoryRow] = useState(null);
  const [intercomAppId, setIntercomAppId] = useState("");
  const [search, setSearch] = usePersistedState("whos-in.search", "");
  // Iter 137 — multi-team filter (empty = all), remembered between sessions.
  const [teamFilter, setTeamFilter] = useState(() => {
    try { return JSON.parse(localStorage.getItem("whos-in-teams") || "[]"); } catch { return []; }
  });
  const setTeams = (v) => {
    setTeamFilter(v);
    try { localStorage.setItem("whos-in-teams", JSON.stringify(v)); } catch { /* ignore */ }
  };
  const [statusFilter, setStatusFilter] = usePersistedState("whos-in.status", "all");
  // Iter 114s — the pill is staff-only AND requires the In/Out view grant.
  const isStaff = user?.base_role === "staff" && has("inout.view");

  const load = () => api.get("/inout")
    .then((r) => {
      setStaff(r.data.staff || []);
      setIntercomAppId(r.data.intercom_app_id || "");
    })
    .catch(() => setStaff([]));
  useEffect(() => { if (isStaff) load(); }, [isStaff]);
  useEffect(() => { if (open) load(); }, [open]);

  const counts = useMemo(() =>
    ORDER.reduce((a, s) => { a[s] = staff.filter((x) => x.status === s).length; return a; }, {}),
    [staff],
  );
  const inNow = counts.in || 0;

  const teamsOf = (s) => (s.teams?.length ? s.teams : (s.team ? [s.team] : []));
  const teams = useMemo(() => Array.from(new Set(staff.flatMap(teamsOf))).sort(), [staff]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return staff.filter((s) => {
      if (statusFilter !== "all" && s.status !== statusFilter) return false;
      if (teamFilter.length && !teamsOf(s).some((t) => teamFilter.includes(t))) return false;
      if (needle && !`${s.name || ""} ${teamsOf(s).join(" ")} ${roleLabel(s.role) || ""}`.toLowerCase().includes(needle)) return false;
      return true;
    }).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [staff, search, teamFilter, statusFilter]);

  if (!isStaff) return null;

  return (
    <>
      <button data-testid="whos-in-trigger" onClick={() => setOpen(true)}
        className="relative flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted">
        <Users className="h-4 w-4 text-primary" />
          <span className="hidden sm:inline">Who&apos;s in</span>
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-1.5 text-[11px] font-semibold text-emerald-700 dark:text-emerald-300">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />{inNow}
        </span>
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="flex w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl" data-testid="whos-in-drawer">
          <SheetHeader className="border-b border-border px-5 py-4">
            <SheetTitle className="flex items-center gap-2 text-base">
              <Users className="h-5 w-5 text-primary" /> Who&apos;s in right now
            </SheetTitle>
            <SheetDescription className="text-xs">Live presence — search and filter the list.</SheetDescription>
          </SheetHeader>

          {/* Summary pills */}
          <div className="flex flex-wrap gap-2 border-b border-border bg-muted/40 px-5 py-3" data-testid="whos-in-summary">
            {ORDER.map((s) => {
              const m = STATUS_META[s];
              return (
                <button key={s} type="button"
                  onClick={() => setStatusFilter((cur) => (cur === s ? "all" : s))}
                  data-testid={`whos-in-summary-${s}`}
                  className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                    statusFilter === s ? `${m.chip} ring-2 ring-offset-1 ring-offset-background` : `${m.chip} hover:brightness-95`
                  }`}>
                  <span className={`h-2 w-2 rounded-full ${m.dot}`} />
                  {m.label} <span className="font-bold">{counts[s] || 0}</span>
                </button>
              );
            })}
            {(counts.oncall || staff.some((s) => s.on_call)) && (
              <span data-testid="drawer-oncall-count" title="Currently on call via incident.io"
                className="inline-flex items-center gap-1.5 rounded-full bg-orange-500/10 px-3 py-1 text-sm font-semibold text-orange-700 dark:text-orange-300 ring-1 ring-orange-500/30">
                <Flame className="h-4 w-4" fill="currentColor" /> On call <span className="font-bold">{staff.filter((s) => s.on_call).length}</span>
              </span>
            )}
            {staff.some((s) => s.presence_conflict) && (
              <span data-testid="drawer-conflict-count"
                title={`Teams and Intercom disagree for: ${staff.filter((s) => s.presence_conflict).map((s) => s.name).join(", ")} — left alone`}
                className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/10 px-3 py-1 text-sm font-semibold text-amber-700 ring-1 ring-amber-500/30 dark:text-amber-300">
                <AlertTriangle className="h-3.5 w-3.5" /> {staff.filter((s) => s.presence_conflict).length} conflict
              </span>
            )}
          </div>


          {/* Toolbar: search + team filter */}
          <div className="flex flex-wrap items-center gap-2 border-b border-border px-5 py-3" data-testid="whos-in-toolbar">
            <div className="relative min-w-[180px] flex-1">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name or team…"
                className="h-8 pl-8 text-xs"
                data-testid="whos-in-search"
              />
            </div>
            <TeamFilter teams={teams} value={teamFilter} onChange={setTeams}
              countFor={(t) => staff.filter((s) => teamsOf(s).includes(t)).length}
              testid="whos-in-team-filter" />
            {teamFilter.length > 0 && (
              <span className="text-[11px] text-muted-foreground" data-testid="whos-in-team-count">
                {filtered.length} of {staff.length}
              </span>
            )}
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto px-5 py-4">
            {filtered.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground" data-testid="whos-in-empty">
                {staff.length === 0 ? "No staff." : "No one matches these filters."}
              </p>
            ) : (
              <div className="divide-y divide-border rounded-lg border border-border" data-testid="whos-in-list">
                <div className="flex items-center gap-2 bg-muted/60 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  <span className="w-8 shrink-0" />
                  <span className="min-w-0 flex-1">Person</span>
                  <span className="w-[76px] shrink-0 text-center">Teams</span>
                  <span className="w-[84px] shrink-0 text-center">Intercom</span>
                  <span className="w-[64px] shrink-0 text-center">On call</span>
                  <span className="w-[84px] shrink-0 text-center">Conduit</span>
                </div>
                {filtered.map((row) => <PersonListRow key={row.user_id} row={row} appId={intercomAppId} />)}
              </div>
            )}
          </div>

          {/* Iter 133b — the On call & handover rota lives on the full In/Out
              board now; the slide-out stays a quick presence list. */}

          <div className="border-t border-border px-5 py-3">
            <button onClick={() => { setOpen(false); navigate("/in-out"); }} data-testid="drawer-view-board"
              className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-border py-2 text-sm font-medium text-primary hover:bg-muted">
              Open the In Out board <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </SheetContent>
      </Sheet>
      <PersonWeekDialog open={!!historyRow} onOpenChange={(o) => !o && setHistoryRow(null)}
        userId={historyRow?.user_id} name={historyRow?.name} />
    </>
  );
};

// One fixed-width status cell so Teams / Intercom / On call / Conduit line up
// as real columns instead of pills of random width.
function Cell({ value, tone, title, width, testid, href }) {
  const cls = `block truncate rounded-full px-1.5 py-0.5 text-center text-[10px] font-semibold ${tone}`;
  return (
    <div className={`${width} shrink-0`}>
      {value ? (
        href ? (
          <a href={href} target="_blank" rel="noreferrer" data-testid={testid}
            title={`${title} — click to open their inbox`} className={`${cls} hover:underline`}>{value}</a>
        ) : (
        <span data-testid={testid} title={title} className={cls}>
          {value}
        </span>)
      ) : (
        <span data-testid={testid} title={title} className="block text-center text-[11px] text-muted-foreground/50">—</span>
      )}
    </div>
  );
}

const PRESENCE_TONES = {
  Available: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  Active: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  Busy: "bg-rose-500/10 text-rose-700 dark:text-rose-300",
  "Do not disturb": "bg-rose-500/10 text-rose-700 dark:text-rose-300",
  "In a meeting": "bg-rose-500/10 text-rose-700 dark:text-rose-300",
  Away: "bg-amber-500/10 text-amber-700 dark:text-amber-300",
  Reassigning: "bg-sky-500/10 text-sky-700 dark:text-sky-300",
};
const pillTone = (v) => PRESENCE_TONES[v] || "bg-slate-500/10 text-slate-600 dark:text-slate-300";

function PersonListRow({ row, appId }) {
  const meta = STATUS_META[row.status] || STATUS_META.out;
  const updated = fmtRelative(row.status_updated_at || row.updated_at);
  const teams = (row.teams_status || "").trim();
  const intercom = (row.intercom_status || "").trim();
  return (
    <div data-testid={`whos-in-listrow-${row.user_id}`}
      className="flex items-center gap-3 bg-card px-3 py-2 hover:bg-muted/50">
      <Avatar name={row.name} src={row.avatar_url} size="sm" fallback="mystery" />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-foreground">
          {row.name}
          <ConflictBadge row={row} testid={`whos-in-listrow-conflict-${row.user_id}`} className="ml-1 align-middle" />
        </div>
        <div className="truncate text-[11px] text-muted-foreground">
          {(row.teams || []).length ? row.teams.join(", ") : (row.team || roleLabel(row.role) || "—")}
          {updated && <span className="ml-2">· {updated}</span>}
        </div>
      </div>
      <Cell testid={`whos-in-listrow-teams-${row.user_id}`} width="w-[76px]"
        value={teams} tone={pillTone(teams)} title={teams ? `Microsoft Teams: ${teams}` : "No Teams presence"} />
      <Cell testid={`whos-in-listrow-intercom-${row.user_id}`} width="w-[84px]"
        value={intercom} tone={pillTone(intercom)}
        title={intercom ? `Intercom: ${intercom}` : "Not an Intercom teammate"} />
      <div className="flex w-[64px] shrink-0 justify-center">
        {row.on_call ? (
          <span data-testid={`whos-in-listrow-oncall-${row.user_id}`} title={handoverTitle(row)}
            className="inline-flex items-center gap-1 rounded-full bg-orange-500/10 px-1.5 py-0.5 text-[10px] font-semibold text-orange-600 dark:text-orange-300">
            <Flame className="h-2.5 w-2.5" fill="currentColor" /> On
          </span>
        ) : <span className="text-[11px] text-muted-foreground/50">—</span>}
      </div>
      <div className="flex w-[84px] shrink-0 justify-center">
        <span className={`inline-flex w-full items-center justify-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] font-semibold ${meta.chip}`}
          data-testid={`whos-in-listrow-status-${row.user_id}`}>
          <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${meta.dot}`} />
          {meta.label}
        </span>
      </div>
    </div>
  );
}
